<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="showLoader || !filteredData.length"
    :showLoader="showLoader"
	:showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :showSearchSlot="!showLoader && getOperators.length && !applyErrorState"
    :showTopRightAreaSlot="getOperators.length && !showLoader"
    :hideHeaderButtons="!getOperators.length || showLoader"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- We are using this slot, even tho the name is not that clear, it's the position we need for the header -->
        <template #accounts-card-slot-area>
            <button-primary
            v-if="getOperators.length && !showLoader && showCreateButton"
            icon="add-user"
            buttonSize="medium"
            :label="t('approvals.operators.createNewBtn')"
            @clicked="navigateByName(routeList.APPROVALS_OPERATORS_CREATE_NAME)"/>
        </template>
        <!-- *********************************************
        *                    Search Bar                  *
        ********************************************** -->
        <template #top-search-area-slot>
            <input-search
            v-if="!showLoader && getOperators.length && !applyErrorState"
            :params="{ placeholder: t('approvals.operators.searchPlaceholder')}"
            :inputValue="searchValue"
            :preventInputClick="getMobileAppState"
            @input="searchValue = $event"
            @rightButtonClicked="searchValue = ''"
            @clicked="showMobileSearch = true"/>
        </template>
        <div
        v-if="!showLoader && filteredData.length && !applyErrorState" class="content-animation prevent-auto-margins">
            <!-- *********************************************
            *                  Operators Table               *
            ********************************************** -->
            <table-generic
            :headerData="operatorsTableHeaderData"
            :tableStyles="operatorsTableStyles"
            :tableData="filteredData"
            @sortBy="sortByInfo = $event"
            @rowClicked="tableClickHandler($event)"
            @buttonClicked="redirectToEditFeature"
            @moreOptionsClicked="moreOptionsClickHandler"/>
        </div>
        <!-- *********************************************
        *                    Empty State                 *
        ********************************************** -->
		<template v-if="!applyErrorState && !showLoader && (!getOperators.length || (!filteredData.length && getOperators.length))" #empty-state-slot>
            <empty-state-generic
            v-if="!filteredData.length && getOperators.length"
            illustration="emptyState/search"
            :title="t('approvals.operators.emptyStateTitle')"
            :description="t('approvals.operators.emptyStateDescription')"/>
            <empty-state-generic
            v-if="!getOperators.length"
            buttonIcon="add-user"
            illustration="emptyState/operators-light"
            :hasButton="showCreateButton"
            :title="t('approvals.operators.noOperatorsEmptyStateTitle')"
            :description="t('approvals.operators.noOperatorsEmptyStateDescription')"
            :buttonLabel="t('approvals.operators.createNewBtn')"
            @clicked="navigateByName(routeList.APPROVALS_OPERATORS_CREATE_NAME)"/>
        </template>
    </wrapper-features-templates-one-column>
    <!-- Operator Details -->
	<operator-details
	v-if="showDetailsModal"
	:operator="operator"
	@hideMe="showDetailsModal = false"/>
    <!-- Search Modal for Mobile -->
    <modal-side-search
    v-if="showMobileSearch"
    clearSearchValueOnClose
    :searchPlaceholder="t('approvals.operators.searchPlaceholder')"
    :searchInputValue="searchValue"
    :triggerClose="triggerMobileSearchClose"
    :applyEmptyState="!filteredData.length"
    @updateSearchValue="searchValue = $event"
    @hideMe="[showMobileSearch = false, triggerClose = false, searchValue = '']">
        <table-generic
        :headerData="operatorsTableHeaderData"
        :tableStyles="operatorsTableStyles"
        :tableData="filteredData"
        @sortBy="sortByInfo = $event"
        @rowClicked="tableClickHandler($event)"
        @moreOptionsClicked="moreOptionsClickHandler"/>
    </modal-side-search>
    <!-- Error Feedback -->
	<toasts-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import ButtonPrimary from '@/components/generic/buttons/Primary'
import InputSearch from '@/components/generic/inputs/Search'
import TableGeneric from '@/components/generic/table/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import OperatorDetails from '@/components/features/approvals/operators/details/OperatorDetails'
import ModalSideSearch from '@/components/generic/modals/side/Search'
import ToastsGeneric from '@/components/generic/toasts/Generic'
/* Assets & Helpers */
import { ref, computed, onBeforeUnmount } from 'vue'
import { iconHandler, classesHandler } from '@/assets/scripts/mappers/operators'
import { sortTableData } from '@/assets/scripts/parsers/tables'
import { getCurrentRouteMenuItemInfo } from '@/assets/scripts/parsers/menu'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestOperators, clearFeatureStates } = mapActions(moduleMapper.APPROVALS_OPERATORS)
const { getOperators } = mapGetters(moduleMapper.APPROVALS_OPERATORS)
const { getMobileAppState } = mapGetters()

/* Data */
const showOverlayLoader = ref(false)
const showLoader = ref(false)
const applyErrorState = ref(false)

const showCreateButton = computed(() => {
    return getCurrentRouteMenuItemInfo(routeList.APPROVALS_OPERATORS_CREATE_PATH)
})

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestOperatorsList = async () => {
	showLoader.value = true
	applyErrorState.value = false
	showFeedbackToast.value = false
	cancelAllPendingRequests()

	try {
        await requestOperators()

        showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		showLoader.value = false
		applyErrorState.value = true
		errorHandler(error.response.data, requestOperatorsList)
	}
}

/*************************************************
*                                                *
*                 Operators Data                 *
*                                                *
*************************************************/
const showDetailsModal = ref(false)
const showMobileSearch = ref(false)
const triggerMobileSearchClose = ref(false)
const operator = ref(null)
const sortByInfo = ref()

const operatorsMoreOptions = operator => {
    const moreOptions = [
        {
            id: 2,
            text: operator.status?.operatorStatusValue === 4 ? t('approvals.operators.moreOptions.unblock') : t('approvals.operators.moreOptions.block'),
            icon: operator.status?.operatorStatusValue === 4 ? 'unlock' : 'block',
            callbackFunction: () => {
                if (operator.status?.operatorStatusValue === 4) {
                    navigateByName(routeList.APPROVALS_OPERATORS_UNBLOCK_NAME, { operatorId: operator.id })
                } else {
                    navigateByName(routeList.APPROVALS_OPERATORS_BLOCK_NAME, { operatorId: operator.id })
                }
            }
        }
    ]

    if (!operator.condition) {
        moreOptions.push({
            id: 3,
            text: t('approvals.operators.moreOptions.delete'),
            icon: 'delete',
            variant: 'negative',
            callbackFunction: () => navigateByName(routeList.APPROVALS_OPERATORS_DELETE_NAME, { operatorId: operator.id })
        })
    }
    return moreOptions
}

const operatorsTableHeaderData = [
	{ label: t('approvals.operators.tableHeader.name'), dataAttr: 'name', rowCellType: 'title', sortable: !getMobileAppState.value, showInMobile: { groupWith: ['username', 'status'] } },
    { label: t('approvals.operators.tableHeader.profile'), dataAttr: 'profile', rowCellType: 'description', sortable: !getMobileAppState.value },
	{ label: t('approvals.operators.tableHeader.username'), dataAttr: 'username', rowCellType: 'description', sortable: !getMobileAppState.value },
    { label: t('approvals.operators.tableHeader.status'), dataAttr: 'status', rowCellType: 'description', sortable: !getMobileAppState.value },
    { label: t('approvals.operators.tableHeader.condition'), dataAttr: 'condition', rowCellType: 'status', sortable: !getMobileAppState.value, showInMobile: true },
    { dataAttr: 'button', rowCellType: 'button' },
    { dataAttr: 'tableOptions', rowCellType: 'more-options', showInMobile: true }
]

const operatorsTableStyles = [null, null, { width: '120px', 'min-width': '120px' }, null, null, null, null]

const operatorsTableData = computed(() => {
    return getOperators.value.map(operator => {
		return [
            { value: operator.id, dataAttr: 'id' },
            { type: 'title', value: operator.name, dataAttr: 'name' },
			{
                type: 'description',
                value: !operator.profile ? '-' : `${getMobileAppState.value ? t('approvals.operators.tableHeader.profileMobile') : ''}` + operator.profile?.operatorProfileDescription || '-',
                dataAttr: 'profile'
            },
            {
                type: 'description',
                value: `${getMobileAppState.value ? t('approvals.operators.tableHeader.usernameMobile') : ''}` + operator.username,
                dataAttr: 'username'
            },
            {
                type: 'description',
                value: !operator.status ? '-' : `${getMobileAppState.value ? t('approvals.operators.tableHeader.statusMobile') : ''}` + operator.status?.operatorStatusDescription || '-',
                dataAttr: 'status'
            },
            {
                type: 'status',
                value: {
                    text: operator.condition || '',
                    icon: operator.condition ? 'clock' : iconHandler(operator.status?.operatorStatusValue),
                    classes: operator.condition ? 'warning' : classesHandler(operator.status?.operatorStatusValue)
                },
                dataAttr: 'condition'
            },
            { type: 'button', value: 'pencil', dataAttr: 'button', hidden: operator.condition },
            {
                type: 'more-options',
                value: operatorsMoreOptions(operator),
                dataAttr: 'tableOptions',
                hidden: operator.condition && operator.status?.operatorStatusValue === 4
            }
        ]
	})
})

const searchValue = ref('')

const filteredData = computed(() => {
    let filteredData = operatorsTableData.value.filter(ele => ele.find(el => el.dataAttr === 'name').value.toLowerCase().includes(searchValue.value.toLowerCase()))
	if (sortByInfo.value) filteredData = sortTableData(filteredData, sortByInfo.value)

	return filteredData
})

const tableClickHandler = row => {
	operator.value = getOperators.value.find(operator => operator.id === row.rowId)

	showDetailsModal.value = true
}

const moreOptionsClickHandler = row => {
    operator.value = getOperators.value.find(operator => operator.id === row.rowId)

    operatorsMoreOptions(operator.value).find(obj => obj.id === row.optionClicked).callbackFunction()
}

const redirectToEditFeature = event => {
    navigateByName(routeList.APPROVALS_OPERATORS_EDIT_NAME, { operatorId: event.rowId })
}
/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

requestOperatorsList()

onBeforeUnmount(() => {
	clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.content-animation {
	@include commonEnteringAnimation;
}
</style>
